import React, { useEffect } from 'react'
import { createStyles, makeStyles } from '@material-ui/core/styles'
import Box from '@material-ui/core/Box'
import Table from '@material-ui/core/Table'
import TableBody from '@material-ui/core/TableBody'
import TableCell from '@material-ui/core/TableCell'
import TableHead from '@material-ui/core/TableHead'
import TableRow from '@material-ui/core/TableRow'
import diff from 'object-diff'
import { useDispatch, useSelector } from 'react-redux'
import { useForm, useField } from 'react-final-form-hooks'

import i18n from '../../../i18n'
import styles from '../../../styles'
import { Order } from '../../../store/orders/types'
import Typography from '@material-ui/core/Typography'
import classNames from 'classnames'
import * as questionnaireSelectors from '../../../selectors/questionnaire'
import { ReadOnlyTextField, TextField } from '../../common/formHelpers'
import { AppState } from '../../../store'
import { Client } from '../../../store/clients/reducer'
import Button from '../../common/Button'
import { updateClient, fetchExpertInterview } from '../../../store/clients/actions'
import { Questionnaire } from '../../../store/orders/questionnaire/types'
import { updateQuestionnaire } from '../../../store/questionnaire/actions'
import { ExpertInterviewData } from '../../../store/clients/types'

const useStyles = makeStyles(() =>
  createStyles({
    container: {
      width: '100%',
      display: 'grid',
      gridRowGap: '2rem',
      gridTemplateColumns: 'auto auto',
      '@media (max-width: 1215px)': {
        gridTemplateColumns: 'auto',
      },
      '@media (max-width: 1025px)': {
        gridTemplateColumns: 'auto auto',
      },
      '@media (max-width: 805px)': {
        gridTemplateColumns: 'auto',
      },
      '& a': {
        color: styles.palette.link.color,
      },
    },
    saveContainer: {
      display: 'flex',
    },
    saveButton: {
      marginLeft: 'auto',
    },
    title: {
      fontSize: '1.3125rem',
      marginBottom: '1.75rem',
      fontWeight: 'bold',
    },
    achivementTitle: {
      marginBottom: '0.5rem',
      color: styles.palette.lightBlack.color,
      fontWeight: 'bold',
    },
    achivementsList: {
      marginBottom: '1.4375rem',
      color: '#5b5b64',
      listStyle: 'disc',
      '& li': {
        whiteSpace: 'pre-line',
      },
    },
    hr: {
      width: '100%',
      height: '1px',
      margin: '2.4375rem 0',
      background: '#d7dbe1',
    },
    question: {
      color: styles.palette.lightBlack.color,
      marginBottom: '1rem',
      fontWeight: 'bold',
    },
    answer: {
      color: '#5b5b64',
      marginBottom: '2.625rem',
      whiteSpace: 'pre-line',
      '& a': {
        color: styles.palette.link.color,
      },
    },
    linkedinContainer: {
      display: 'flex',
      alignItems: 'flex-start',
      marginTop: '2.4375rem',
    },
    expertInterviewNotes: {
      marginTop: '0.625rem',
      padding: '1rem',
      backgroundColor: '#f8f9fa',
      borderLeft: '4px solid #007bff',
      fontSize: '0.875rem',
      lineHeight: 1.6,
      color: '#333',
      maxHeight: '300px',
      overflowY: 'auto',
      whiteSpace: 'pre-wrap',
    },
    expertInterviewTableContainer: {
      marginTop: '0.625rem',
      overflowX: 'auto',
      width: '100%',
    },
    expertInterviewTable: {
      minWidth: '500px',
      '& th': {
        backgroundColor: '#f5f5f5',
        fontWeight: 600,
        whiteSpace: 'nowrap',
      },
      '& td': {
        '@media (max-width: 600px)': {
          fontSize: '0.75rem',
          padding: '6px 8px',
        },
      },
      '& a': {
        color: styles.palette.link.color,
        wordBreak: 'break-word',
      },
    },
  })
)

interface ClientDetailsProps {
  order: Order
  questionnaire: Questionnaire | undefined
}

export interface ClientInfoUpdate {
  name: string
  email: string
}

export interface ClientLinkedinUpdate {
  linkedin: string
}

// A lot of this data is also on the order/customer record. This pane tries to ignore that data and pull from the
// questionnaire, as it often as differing information on repeat customers and is somewhat more accurate. That said, if
// you update something line a customer's email address in OMS, it will not be reflected here and that is on purpose.
const ClientDetails: React.FC<ClientDetailsProps> = ({ order, questionnaire }) => {
  const classes = useStyles()
  const [hasErrors, setHasErrors] = React.useState(false)
  const [hasLinkedinError, setHasLinkedinError] = React.useState(false)
  const [editLinkedin, setEditLinkedin] = React.useState(false)
  const dispatch = useDispatch()

  const clientData = useSelector<AppState, Client>(state => state.clientReducer.clients[order.client_id])
  const expertInterviewData = useSelector<AppState, ExpertInterviewData | undefined>(
    state => state.clientReducer.expertInterviews[order.client_id]
  )

  // Fetch expert interview data on mount
  useEffect(() => {
    dispatch(fetchExpertInterview(order.client_id))
  }, [dispatch, order.client_id])

  const industry = questionnaireSelectors.getIndustry(questionnaire)
  const address = questionnaireSelectors.getAddress(questionnaire)
  const phoneNumbers = questionnaireSelectors.getPhoneNumbers(questionnaire)
  const linkedinURL = questionnaireSelectors.getLinkedInURL(questionnaire)
  const country = questionnaireSelectors.getCountry(questionnaire)
  const concerns = questionnaireSelectors.getConcerns(questionnaire)
  const accomplishments = questionnaireSelectors.getAccomplishments(questionnaire)
  const accomplishmentsTexts = questionnaireSelectors.getAccomplishmentsTexts(questionnaire)
  const documents = questionnaireSelectors.getAdditionalDocuments(questionnaire)
  const hasConcerns = concerns.length > 0
  const hasAccomplishments = accomplishments.length > 0
  const hasAccomplishmentsTexts = accomplishmentsTexts.length > 0
  const hasDocuments = documents.length > 0
  // @TODO This isn't the exact brand name from Products, which is what the current version of the OMS shows here,
  // I think. We maybe need to return that from the API, if this isn't good enough.
  const brandName = order.brand.name

  // @TODO We need to hide the address, phone number, and LinkedIn URL from FCC admins

  const validate = (values: ClientInfoUpdate) => {
    const errors: { [key: string]: string } = {}

    Object.entries(values).forEach(([key, value]) => {
      if (!value) {
        errors[key] = i18n.t('account__error__required')
      }
    })

    if (!values.email) {
      errors.email = i18n.t('account__error__required')
    } else if (!values.email.includes('@')) {
      errors.email = i18n.t('account__error__email')
    }

    setHasErrors(Object.keys(errors).length > 0)
    return errors
  }

  const onSubmit = (data: ClientInfoUpdate): Promise<void> => {
    return new Promise(async resolve => {
      const oldClient: ClientInfoUpdate = { name: clientData.full_name, email: clientData.primary_email }

      const diffedData = diff(oldClient, data)

      if (!Object.keys(diffedData).length) {
        return resolve()
      }

      await dispatch(updateClient(order.client_id, diffedData))
      resolve()
    })
  }

  const { form, handleSubmit, pristine, submitting } = useForm({ onSubmit, validate })

  const name = useField('name', form)
  const email = useField('email', form)

  useEffect(() => {
    form.initialize({ name: clientData.full_name, email: clientData.primary_email })
  }, [form, clientData])

  const validateLinkedin = (values: ClientLinkedinUpdate) => {
    setHasLinkedinError(!values.linkedin)
    return undefined
  }

  const onLinedinSubmit = (data: ClientLinkedinUpdate): Promise<void> => {
    return new Promise(async resolve => {
      if (linkedinURL === data.linkedin) {
        return resolve()
      }

      try {
        await dispatch(
          updateQuestionnaire(
            order.client_id,
            {
              question_id: 18,
              answer: data.linkedin,
            },
            'linkedinLink'
          )
        )
      } catch (error) {
        setTimeout(() => linkedinForm.reset())
      }

      setEditLinkedin(false)
      resolve()
    })
  }

  const {
    form: linkedinForm,
    handleSubmit: handleLinkedinSubmit,
    pristine: pristineLinkedin,
    submitting: submittingLinkedin,
  } = useForm({ onSubmit: onLinedinSubmit, validate: validateLinkedin })

  const linkedin = useField('linkedin', linkedinForm)

  useEffect(() => {
    linkedinForm.initialize({ linkedin: linkedinURL ?? '' })
  }, [linkedinForm, linkedinURL])

  return (
    <>
      <form onSubmit={handleSubmit}>
        <Box className={classes.container}>
          <TextField field={name} label={i18n.t('order__page__client__details__name')} />
          <TextField field={email} label={i18n.t('order__page__client__details__email')} htmlType={'email'} />

          {!!address && (
            <ReadOnlyTextField label={i18n.t('order__page__client__details__address')} text={address.join('\n')} />
          )}
          {!!country && <ReadOnlyTextField label={i18n.t('order__page__client__details__country')} text={country} />}
          {!!phoneNumbers && (
            <ReadOnlyTextField
              label={i18n.t('order__page__client__details__phone')}
              text={phoneNumbers
                .filter(pn => !!pn.number)
                .map(pn => `${i18n.t(`order__page__client__details__phone__type__${pn.type}`)}: ${pn.number}`)
                .join(', ')}
            />
          )}
          {!!industry && <ReadOnlyTextField label={i18n.t('order__page__client__details__industry')} text={industry} />}
          <ReadOnlyTextField label={i18n.t('order__page__client__details__source')} text={brandName} />
        </Box>
        <Box className={classes.saveContainer}>
          <Button
            className={classes.saveButton}
            type="primary"
            disabled={submitting || pristine || hasErrors}
            onClick={() => form.submit()}
            showLoader={submitting}
            area-label="save_changes"
          >
            {i18n.t('account__save')}
          </Button>
        </Box>
      </form>
      <form className={classes.linkedinContainer} onSubmit={handleLinkedinSubmit}>
        {!editLinkedin && !!linkedinURL ? (
          <ReadOnlyTextField label={i18n.t('order__page__client__details__linkedin')} text={linkedinURL} />
        ) : (
          <TextField field={linkedin} label={i18n.t('order__page__client__details__linkedin')} />
        )}
        {!editLinkedin && !!linkedinURL ? (
          <Button
            className={classes.saveButton}
            type="primary"
            onClick={() => setEditLinkedin(true)}
            area-label="edit_linkedin"
          >
            {i18n.t('account__linkedin__edit')}
          </Button>
        ) : (
          <Button
            className={classes.saveButton}
            type="primary"
            disabled={submittingLinkedin || pristineLinkedin || hasLinkedinError}
            onClick={() => linkedinForm.submit()}
            showLoader={submitting}
            area-label="save_linkedin"
          >
            {i18n.t('account__linkedin__save')}
          </Button>
        )}
        <Box className={classes.saveContainer}></Box>
      </form>

      {/* Expert Interview Section */}
      {expertInterviewData?.has_expert_interview && (
        <>
          <hr className={classes.hr} />
          <Typography className={classes.title}>Expert Interview Information</Typography>

          {expertInterviewData.interview_notes && (
            <Box>
              <Typography variant={'body1'} className={classes.question}>
                Interview Notes
              </Typography>
              <Box className={classes.expertInterviewNotes}>{expertInterviewData.interview_notes}</Box>
            </Box>
          )}

          {expertInterviewData.attachments && expertInterviewData.attachments.length > 0 && (
            <Box style={{ marginTop: '1.5rem' }}>
              <Typography variant={'body1'} className={classes.question}>
                Interview Attachments
              </Typography>
              <Box className={classes.expertInterviewTableContainer}>
                <Table className={classes.expertInterviewTable} size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>File</TableCell>
                      <TableCell>Size</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>Uploaded At (UTC)</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {expertInterviewData.attachments.map(attachment => {
                      const fileSizeKB = attachment.file_size / 1024
                      const fileSizeFormatted =
                        fileSizeKB > 1024 ? `${(fileSizeKB / 1024).toFixed(2)} MB` : `${fileSizeKB.toFixed(2)} KB`

                      // Simplify mime type to file type
                      let fileType = 'File'
                      const mimeType = attachment.mime_type || ''
                      if (mimeType.startsWith('image/')) {
                        fileType = 'Image'
                      } else if (mimeType.startsWith('video/')) {
                        fileType = 'Video'
                      } else if (mimeType.startsWith('audio/')) {
                        fileType = 'Audio'
                      } else if (
                        mimeType.startsWith('text/') ||
                        mimeType.startsWith('application/pdf') ||
                        mimeType.startsWith('application/msword') ||
                        mimeType.startsWith('application/vnd.openxmlformats-officedocument') ||
                        mimeType.startsWith('application/vnd.ms-') ||
                        mimeType.startsWith('application/rtf')
                      ) {
                        fileType = 'Document'
                      }

                      const downloadUrl = `/api/v1/clients/${order.client_id}/expert-interview/attachment/${attachment.id}`
                      const uploadedAt = new Date(attachment.created_at).toLocaleString()

                      return (
                        <TableRow key={attachment.id}>
                          <TableCell>
                            <a href={downloadUrl} target="_blank" rel="noopener noreferrer">
                              {attachment.file_name}
                            </a>
                          </TableCell>
                          <TableCell>{fileSizeFormatted}</TableCell>
                          <TableCell>{fileType}</TableCell>
                          <TableCell>{uploadedAt}</TableCell>
                        </TableRow>
                      )
                    })}
                  </TableBody>
                </Table>
              </Box>
            </Box>
          )}
        </>
      )}

      <Box>
        {hasConcerns && (
          <>
            <hr className={classes.hr} />
            <Typography className={classes.title}>
              {i18n.t('order__page__client__details__primary__concerns')}
            </Typography>
            {concerns.map((c, i) => (
              <Box key={i}>
                <Typography variant={'body1'} className={classes.question}>
                  {c.question}
                </Typography>
                {c.answer.map((answer, ai) => (
                  <Typography variant={'body2'} key={`concern-answer-${i}-${ai}`} className={classes.answer}>
                    {answer}
                  </Typography>
                ))}
              </Box>
            ))}
          </>
        )}

        {(hasAccomplishments || hasAccomplishmentsTexts) && (
          <>
            <hr className={classes.hr} />
            <Typography className={classes.title}>
              {i18n.t('order__page__client__details__proudest__achievements')}
            </Typography>
            {accomplishments.map((accomplishment, i) => (
              <Box key={`accomplishment-${i}`}>
                <Typography variant={'body1'} className={classes.question}>
                  {[accomplishment.title, accomplishment.org_name].filter(a => !!a).join(', ')}
                </Typography>
                <Typography variant={'body2'} className={classes.achivementsList} component={'ul'}>
                  {accomplishment.list &&
                    accomplishment.list.length > 0 &&
                    accomplishment.list.map((a, ai) => (
                      <Typography key={`accomplishment-${i}-${ai}`} component={'li'} variant={'body2'}>
                        {a}
                      </Typography>
                    ))}
                </Typography>
              </Box>
            ))}
            {accomplishmentsTexts.map((at, i) => (
              <Box key={`accomplishments-text-${i}`}>
                <Typography variant={'body1'} className={classes.question}>
                  {at.question}
                </Typography>
                {at.answer.map((answer, ai) => (
                  <Typography
                    variant={'body2'}
                    key={`accomplishments-text-answer-${i}-${ai}`}
                    className={classes.answer}
                  >
                    {answer}
                  </Typography>
                ))}
              </Box>
            ))}
          </>
        )}

        {hasDocuments && (
          <>
            <hr className={classes.hr} />
            <Typography className={classes.title}>{i18n.t('order__page__client__details__documents')}</Typography>
            {documents.map((docs, i) => (
              <Box key={i}>
                <Typography variant={'body1'} className={classes.question}>
                  {docs.question}
                </Typography>
                <Typography
                  variant={'body2'}
                  className={classNames([classes.achivementsList, classes.answer])}
                  component={'ul'}
                >
                  {docs.answer.map((doc, di) => (
                    <Typography key={`additional-doc-${i}-${di}`} variant={'body2'} component={'li'}>
                      <a href={doc.download_url} className="file-download-link">
                        {doc.originalName}
                      </a>
                    </Typography>
                  ))}
                </Typography>
              </Box>
            ))}
          </>
        )}
      </Box>
    </>
  )
}

export default React.memo(ClientDetails)
